import React, { useEffect, useMemo, useState } from 'react';
import { studentReportService } from '../services/StudentReportService';
import MapView from './MapView';
import './AdminStudentReportsView.css';
import './AdminMoreDetailPages.css';

const STATUS_OPTIONS = [
  { value: 'all', label: 'All Reports' },
  { value: 'pending', label: 'Pending' },
  { value: 'resolved', label: 'Resolved' }
];

function AdminStudentReportsView({ adminData }) {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedReport, setSelectedReport] = useState(null);
  const [resolutionNotes, setResolutionNotes] = useState('');
  const [resolving, setResolving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setLoading(true);
    const unsubscribe = studentReportService.subscribeToReports((adminData?.schoolIds?.length ? adminData.schoolIds : adminData?.schoolId) || null, (data) => {
      setReports(data);
      setLoading(false);
    });
    return () => unsubscribe();
  }, [adminData]);

  const filteredReports = useMemo(() => {
    if (statusFilter === 'all') return reports;
    return reports.filter(r => (r.status || 'pending').toLowerCase() === statusFilter);
  }, [reports, statusFilter]);

  const mapMarkers = useMemo(() => {
    return filteredReports
      .filter(r => Number.isFinite(r.latitude) && Number.isFinite(r.longitude) && (r.latitude !== 0 || r.longitude !== 0))
      .map(r => ({
        lat: r.latitude,
        lng: r.longitude,
        color: (r.status || 'pending').toLowerCase() === 'resolved' ? '#10b981' : '#ef4444',
        label: `<div><strong>${r.studentName || 'Unknown'}</strong><br/>${r.reason || 'No reason'}<br/>${r.locationName || ''}<br/>${new Date(r.createdAt).toLocaleString()}</div>`
      }));
  }, [filteredReports]);

  const handleResolve = async () => {
    if (!selectedReport) return;
    setResolving(true);
    setError('');
    try {
      await studentReportService.resolveReport(selectedReport.id, adminData?.uid || '', resolutionNotes);
      setSelectedReport(null);
      setResolutionNotes('');
    } catch (err) {
      console.error('Error resolving report:', err);
      setError('Failed to resolve report. Please try again.');
    } finally {
      setResolving(false);
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading reports...</p>
      </div>
    );
  }

  // === Full-screen report detail page ===
  if (selectedReport) {
    const r = selectedReport;
    const isResolved = (r.status || 'pending').toLowerCase() === 'resolved';
    const hasCoords = Number.isFinite(r.latitude) && Number.isFinite(r.longitude) && (r.latitude !== 0 || r.longitude !== 0);
    const detailMarkers = hasCoords ? [{
      lat: r.latitude,
      lng: r.longitude,
      color: isResolved ? '#10b981' : '#ef4444',
      label: `<div><strong>${r.studentName || 'Unknown'}</strong><br/>${r.reason || ''}<br/>${r.locationName || ''}</div>`
    }] : [];
    console.log('🔥 AdminStudentReports: Showing detail for report', r.id, r);
    return (
      <div className="admin-student-reports-view detail-page">
        <button
          className="detail-back-button"
          onClick={() => { setSelectedReport(null); setResolutionNotes(''); setError(''); }}
        >
          ◄ Back to Student Reports
        </button>
        <div className="detail-card">
          <div className="detail-header">
            <div>
              <h2>{r.studentName || 'Unknown Student'}</h2>
              <p className="detail-subtitle">{r.reportType || 'Student Report'}</p>
            </div>
            <span className={`detail-status-badge`} style={{
              background: isResolved ? '#d1fae5' : '#fef3c7',
              color: isResolved ? '#065f46' : '#92400e'
            }}>
              {r.status || 'pending'}
            </span>
          </div>

          {error && <div className="error-message">{error}</div>}

          <div className="detail-grid">
            <div className="detail-field">
              <span className="detail-label">Student</span>
              <span className="detail-value">{r.studentName || 'Unknown'}</span>
            </div>
            {r.studentId && (
              <div className="detail-field">
                <span className="detail-label">Student ID</span>
                <span className="detail-value">{r.studentId}</span>
              </div>
            )}
            <div className="detail-field">
              <span className="detail-label">Reason</span>
              <span className="detail-value">{r.reason || 'N/A'}</span>
            </div>
            <div className="detail-field">
              <span className="detail-label">Location</span>
              <span className="detail-value">{r.locationName || 'Unknown location'}</span>
            </div>
            <div className="detail-field">
              <span className="detail-label">Reported By (Scanner)</span>
              <span className="detail-value">{r.scannerName || r.scannerId || 'N/A'}</span>
            </div>
            <div className="detail-field">
              <span className="detail-label">Reported</span>
              <span className="detail-value">{r.createdAt ? new Date(r.createdAt).toLocaleString() : 'N/A'}</span>
            </div>
            {hasCoords && (
              <div className="detail-field">
                <span className="detail-label">Coordinates</span>
                <span className="detail-value">{r.latitude.toFixed(5)}, {r.longitude.toFixed(5)}</span>
              </div>
            )}
            {r.schoolName && (
              <div className="detail-field">
                <span className="detail-label">School</span>
                <span className="detail-value">{r.schoolName}</span>
              </div>
            )}
          </div>

          {r.notes && (
            <div className="detail-section">
              <h4>Notes</h4>
              <p className="detail-text">{r.notes}</p>
            </div>
          )}
          {r.description && (
            <div className="detail-section">
              <h4>Description</h4>
              <p className="detail-text">{r.description}</p>
            </div>
          )}
          {r.photoURL && (
            <div className="detail-section">
              <h4>Photo</h4>
              <img src={r.photoURL} alt="Report" className="detail-hero" />
            </div>
          )}

          {hasCoords && (
            <div className="detail-section">
              <h4>Report Location</h4>
              <MapView markers={detailMarkers} height={280} defaultZoom={16} />
            </div>
          )}

          {isResolved ? (
            <div className="detail-section">
              <h4>Resolution</h4>
              <p className="detail-text">
                Resolved on {r.resolvedAt ? new Date(r.resolvedAt).toLocaleString() : 'N/A'}
                {r.resolvedBy && ` by ${r.resolvedBy}`}
              </p>
              {r.resolutionNotes && <p className="detail-text">{r.resolutionNotes}</p>}
            </div>
          ) : (
            <>
              <div className="detail-section">
                <h4>Resolution Notes</h4>
                <textarea
                  value={resolutionNotes}
                  onChange={(e) => setResolutionNotes(e.target.value)}
                  placeholder="Add notes before resolving..."
                  rows={3}
                  style={{ width: '100%', padding: '10px', border: '1px solid #d1d5db', borderRadius: '8px', fontFamily: 'inherit' }}
                />
              </div>
              <div className="detail-actions">
                <button
                  className="detail-btn-success"
                  onClick={handleResolve}
                  disabled={resolving}
                >
                  {resolving ? 'Resolving...' : '✓ Mark Resolved'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="admin-student-reports-view">
      <div className="view-header">
        <h2>Student Reports</h2>
        <select
          className="status-filter"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          {STATUS_OPTIONS.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="map-section">
        <MapView markers={mapMarkers} height={480} defaultZoom={14} />
      </div>

      {filteredReports.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">📝</div>
          <h3>No reports found</h3>
          <p>Student reports submitted from the iOS scanner will appear here.</p>
        </div>
      ) : (
        <div className="reports-list">
          {filteredReports.map(report => (
            <div
              key={report.id}
              className={`report-card ${(report.status || 'pending').toLowerCase() === 'resolved' ? 'resolved' : 'pending'}`}
              onClick={() => setSelectedReport(report)}
            >
              <div className="report-main">
                <h4>{report.studentName || 'Unknown Student'}</h4>
                <span className="report-reason">{report.reason}</span>
                <span className="report-location">{report.locationName || 'Unknown location'}</span>
              </div>
              <div className="report-meta">
                <span className={`status-badge ${report.status || 'pending'}`}>{report.status || 'pending'}</span>
                <span className="report-time">{new Date(report.createdAt).toLocaleString()}</span>
              </div>
            </div>
          ))}
        </div>
      )}

    </div>
  );
}

export default AdminStudentReportsView;
